import { orgSelectors } from 'ducks/orgs'
import { DistributorTradePricingContext } from 'elements/hardwareSelectorV2/HardwareSelectorCore'
import { ComponentVersionsInherit, ComponentVersions_3_0, styled, Typography } from 'opensolar-ui'
import { memo } from 'react'
import { useTranslate } from 'react-admin'
import { useSelector } from 'react-redux'
import useDistributorTradePricing from 'services/useDistributorTradePricing'
import useEnabledHardwareSuppliers from '../hooks/useEnabledHardwareSuppliers'
import { useRecommendedProductsFromDesigns } from '../hooks/useRecommendedProductsFromDesigns'
import { useRecommendedProductsFromPurchases } from '../hooks/useRecommendedProductsFromPurchases'
import ProductGrid from './ProductGrid'

// Upper bound for items shown on the full-list page. Matches the backend's default cap
// applied in `RecommendedProductsService` so we never request more than the service is
// willing to return.
const FULL_LIST_LIMIT = 50
const TIMEFRAME_DAYS = 90

type RecommendationSource = 'designs' | 'purchases'

const SOURCE_TITLES: Record<RecommendationSource, string> = {
  designs: 'From Your Designs',
  purchases: 'From Your Past Purchases',
}

const SOURCE_SUBTITLES: Record<RecommendationSource, string> = {
  designs: 'Recommended based on your current projects',
  purchases: "Reorder items you've bought before",
}

const PageContainer = styled('div')({
  background: '#F6F9FC',
  padding: '24px',
  minHeight: '100%',
})

const PageHeader = styled('div')(({ theme }) => ({
  marginBottom: theme.spacing(3),
}))

const PageTitle = styled(Typography)({
  marginBottom: 8,
})

const PageSubtitle = styled(Typography)(({ theme }) => ({
  color: theme.palette.grey[600],
}))

interface DesignsListProps {
  orgId: number
}

const DesignsList = ({ orgId }: DesignsListProps) => {
  const { data, isLoading } = useRecommendedProductsFromDesigns({
    orgId,
    inStock: true,
    limit: FULL_LIST_LIMIT,
    timeframeDays: TIMEFRAME_DAYS,
  })
  const items = data?.recommended_products || []
  return (
    <ProductGrid
      carouselMode={false}
      componentsData={items}
      loading={isLoading}
      pageSize={FULL_LIST_LIMIT}
      category="from-your-designs-full-list"
    />
  )
}

interface PurchasesListProps {
  orgId: number
}

const PurchasesList = ({ orgId }: PurchasesListProps) => {
  const { data, isLoading } = useRecommendedProductsFromPurchases({
    orgId,
    inStock: true,
    limit: FULL_LIST_LIMIT,
    timeframeDays: TIMEFRAME_DAYS,
  })
  const items = data?.recommended_products || []
  return (
    <ProductGrid
      carouselMode={false}
      componentsData={items}
      loading={isLoading}
      pageSize={FULL_LIST_LIMIT}
      category="from-your-past-purchases-full-list"
    />
  )
}

interface RecommendationsListPageProps {
  source: RecommendationSource
}

const RecommendationsListPage = ({ source }: RecommendationsListPageProps) => {
  const translate = useTranslate()
  const orgId = useSelector(orgSelectors.getOrg)?.id || 0
  const title = translate(SOURCE_TITLES[source])
  const subtitle = translate(SOURCE_SUBTITLES[source])

  const { filteredSuppliers: hardwareDistributors } = useEnabledHardwareSuppliers()
  const { distributorTradePricing, isChecking } = useDistributorTradePricing({
    orgId,
    distributors: hardwareDistributors,
  })

  return (
    <ComponentVersionsInherit versions={ComponentVersions_3_0}>
      <DistributorTradePricingContext.Provider value={{ distributorTradePricing, isChecking }}>
        <PageContainer data-testid={`recommendations-list-${source}`}>
          <PageHeader>
            <PageTitle textVariant="h4">{title}</PageTitle>
            <PageSubtitle textVariant="body2">{subtitle}</PageSubtitle>
          </PageHeader>
          {source === 'designs' ? <DesignsList orgId={orgId} /> : <PurchasesList orgId={orgId} />}
        </PageContainer>
      </DistributorTradePricingContext.Provider>
    </ComponentVersionsInherit>
  )
}

export default memo(RecommendationsListPage)
